import canvasSvg from "./assets/icons/canvas.svg";
import gClassroomSvg from "./assets/icons/classroom.svg";
import schoologySvg from "./assets/icons/schoology.svg";

export const TRIAL_PLAN = "trial";
export const BASIC_PLAN = "basic";
export const LITE_PLAN = "lite";
export const PRO_PLAN = "pro";
export const PREMIUM_PLAN = "premium";

export const CLASSROOM_SCOPE = [
  "https://www.googleapis.com/auth/classroom.courses.readonly",
  "https://www.googleapis.com/auth/classroom.coursework.students",
  "https://www.googleapis.com/auth/classroom.rosters.readonly",
  "https://www.googleapis.com/auth/documents",
];

export const LOCAL_STORAGE_FEATURE_KEY = "essaygrader-version";
export const LOCAL_STORAGE_MENU_HIDDEN_KEY = "essaygrader-menu-hidden";
export const CONVERSION_PRICE_KEY = "essaygrader-conversion-price";

// LSK local storage key
export const LSK_PAYMENT_PROCESSING_SESSION_ID =
  "payment_processing_session_id";
export const LSK_SUGGESTION_FORM_SELECTED_RUBRIC =
  "suggestion_form:selectedRubric";

export const SSK_PLAN_CHANGE_PENDING = "plan_change_pending";

// i18n keys for error messages — use i18n.t(errorTable[code]) in consumers
export const errorTable = {
  pausedLimitsExceeded: "errors.pausedLimitsExceeded",
  planWordLimitsExceeded: "errors.planWordLimitsExceeded",
  planEssayLimitsExceeded: "errors.planEssayLimitsExceeded",
  planAdvancedRubricEssaysLimitExceeded:
    "errors.planAdvancedRubricEssaysLimitExceeded",
  missedPermissions: "errors.missedPermissions",
  badGoogleResponse: "errors.badGoogleResponse",
  essayTextMissed: "errors.essayTextMissed",
};

// i18n keys for password reset form text — use $t(passwordResetFormText.key) in consumers
export const passwordResetFormText = {
  successTitle: "auth.emailSent",
  successMessage: "auth.emailSentMessage",
  problemResettingPassword: "auth.problemResettingPassword",
  invalidEmail: "auth.invalidEmail",
};

export const errorCode = {
  emailNotFound: "EMAIL_NOT_FOUND",
};

export const PLAN_INFO = {
  basic: {
    name: "Basic",
    essayLimit: 25,
    essayWordLimit: 1000,
    summaryLimit: 25,
    monthlyPrice: "0",
    yearlyPrice: "0",
    yearlyTotalPrice: "0",
    translationKey: "account.plans.basic",
  },
  lite: {
    name: "Lite",
    essayLimit: 100,
    essayWordLimit: 2000,
    summaryLimit: 100,
    monthlyPrice: "8.99",
    yearlyPrice: "6.99",
    yearlyTotalPrice: "83.88",
    translationKey: "account.plans.lite",
  },
  pro: {
    name: "Pro",
    essayLimit: 350,
    essayWordLimit: 3500,
    summaryLimit: 350,
    monthlyPrice: "19.99",
    yearlyPrice: "14.99",
    yearlyTotalPrice: "179.88",
    translationKey: "account.plans.pro",
  },
  premium: {
    name: "Premium",
    essayLimit: 700,
    essayWordLimit: 7000,
    summaryLimit: 700,
    monthlyPrice: "49.99",
    yearlyPrice: "34.99",
    yearlyTotalPrice: "419.88",
    translationKey: "account.plans.premium",
  },
};

export const LMS_PROVIDERS = {
  canvas: {
    label: "Canvas",
    translationKey: "imports.lms.canvas",
    icon: canvasSvg,
    key: "canvas",
  },
  canvasPersonal: {
    label: "Canvas",
    translationKey: "imports.lms.canvas",
    icon: canvasSvg,
    key: "canvas",
  },
  google: {
    label: "Google Classroom",
    translationKey: "imports.lms.googleClassroom",
    icon: gClassroomSvg,
    key: "google",
  },
  googleClassroom: {
    label: "Google Classroom",
    translationKey: "imports.lms.googleClassroom",
    icon: gClassroomSvg,
    key: "googleClassroom",
  },
  schoology: {
    label: "Schoology",
    translationKey: "imports.lms.schoology",
    icon: schoologySvg,
    key: "schoology",
  },
};

export const SSO_SERVICE = {
  serviceName: "canvas",
  serviceId: "sso",
};

export const PLATFORMS = Object.freeze({
  ESSAYGRADER: "essaygrader",
  GOOGLE: "google",
  CANVAS: "canvas",
  SCHOOLOGY: "schoology",
});

export const SUPPORTED_UPLOAD_SERVICES = ["googleDrive", "oneDrive", "webcam"];

export const WORKSPACE_PLATFORM_LOCATION_URL = {
  [LMS_PROVIDERS.canvas.key]: "canvas",
  [LMS_PROVIDERS.google.key]: "google",
  [LMS_PROVIDERS.googleClassroom.key]: "google",
  [LMS_PROVIDERS.schoology.key]: "schoology",
};

export const ERROR_BREAKDOWN_TYPES = {
  spelling: {
    color: "#027BFF",
    background: "#E6F2FF",
    title: "Spelling",
    translationKey: "essays.errorBreakdown.spelling",
  },
  grammar: {
    color: "#F28C27",
    background: "#FFEAEC",
    title: "Grammar",
    translationKey: "essays.errorBreakdown.grammar",
  },
  sentenceStructure: {
    color: "#14A2B8",
    background: "#EBFAFC",
    title: "Sentence structure",
    translationKey: "essays.errorBreakdown.sentenceStructure",
  },
  coherenceGaps: {
    color: "#328C47",
    background: "#E9FFEE",
    title: "Coherence gaps",
    translationKey: "essays.errorBreakdown.coherenceGaps",
  },
  clarityIssues: {
    color: "#CE316C",
    background: "#FFEAEC",
    title: "Clarity issues",
    translationKey: "essays.errorBreakdown.clarityIssues",
  },
  logicalInconsistencies: {
    color: "#704BD4",
    background: "#F3E8FC",
    title: "Logical inconsistencies",
    translationKey: "essays.errorBreakdown.logicalInconsistencies",
  },
};

export const PRICING_PLANS = {
  teachers: [
    {
      id: "lite",
      name: "lite plan",
      translationKey: "account.pricing.lite",
      discount: 30,
      description: "For those who don't grade a ton of essays",
      monthlyPrice: 8.99,
      yearlyPrice: 83.88,
      button: {
        text: "Select Lite",
        icon: "arrow-right",
      },
      featuresHeading: null,
      features: [
        "Grade 100 submissions per month",
        "2,000 word limit per submission",
        "FERPA, COPPA, SOC 2 Compliance",
        "Comprehensive Grammar reports",
      ],
    },
    {
      id: "pro",
      name: "pro plan",
      translationKey: "account.pricing.pro",
      discount: 30,
      description: "Educators grading a lot of essays per month",
      monthlyPrice: 19.99,
      yearlyPrice: 179.88,
      button: {
        text: "Select Pro",
        icon: "arrow-right",
      },
      featuresHeading: "Everything in Lite Plus:",
      features: [
        "Grade 350 submissions per month",
        "3,500 word limit per submission",
        "Manual AI-writing detection ",
        "Google Classroom integration",
        "Canvas LMS integration ",
        "Schoology LMS integration ",
        "Assignment, Student, and Class level analytics",
      ],
    },
    {
      id: "premium",
      name: "premium plan",
      translationKey: "account.pricing.premium",
      discount: 30,
      description:
        "Busy educators needing full access to our extensive features",
      monthlyPrice: 49.99,
      button: {
        text: "Select Premium",
      },
      yearlyPrice: 419.92,
      featuresHeading: "Everything in Pro Plus:",
      features: [
        "Grade 800 submissions per month",
        "8,000 word limit per submission",
        "Automatic AI-writing and Plagiarism detection",
      ],
    },
    {
      id: "district",
      name: "district",
      translationKey: "account.pricing.district",
      discount: null,
      description: "For Schools & Districts",
      monthlyPrice: 0,
      yearlyPrice: 0,
      button: {
        text: "Contact us",
      },
      featuresHeading: "Everything in District:",
      features: [
        "Number of submissions customized based on contract ",
        "No word limits per submission ",
        "AI-writing and Plagiarism detection ",
        "Prior listed integrations plus integrations with Teams, Blackboard and D2l Brightspace",
        "School and District wide analytics dashboards ",
        "Will sign custom or state-specific Digital Privacy Agreements (DPAs)",
        "White-glove onboarding and Training",
        "Instant Chat Support.",
      ],
      priceNote: "Contact us for discounted pricing",
      buttonVariant: "primary",
      secondaryButton: "Request a quote",
    },
  ],
  institutions: [
    {
      id: "team",
      name: "TEAM",
      translationKey: "account.pricing.team",
      discount: "30% OFF",
      description: "5+ users",
      monthlyPrice: 12.99,
      yearlyPrice: 155.88,
      features: [
        "Choose either Pro or Premium license only",
        "Training and support",
        "Single P.O. for all licenses",
        "Priority video support",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
      ],
      priceNote: "Price starting at:",
      buttonText: "Book a demo",
      buttonVariant: "primary",
      secondaryButton: "Request a quote",
    },
  ],
};

export const FEATURE_LABELS = [
  {
    label: "Grade essays per month",
    translationKey: "account.featureLabels.gradeEssays",
  },
  {
    label: "Summarize essays per month",
    translationKey: "account.featureLabels.summarizeEssays",
  },
  {
    label: "Essay word count limit",
    translationKey: "account.featureLabels.wordCountLimit",
  },
  {
    label: "Integrations",
    translationKey: "account.featureLabels.integrations",
  },
  {
    label: "Advanced rubrics",
    translationKey: "account.featureLabels.advancedRubrics",
  },
  {
    label: "AI detection",
    translationKey: "account.featureLabels.aiDetection",
  },
  {
    label: "Bulk essay upload",
    translationKey: "account.featureLabels.bulkUpload",
  },
  {
    label: "Shared rubric library",
    translationKey: "account.featureLabels.sharedRubricLibrary",
  },
  {
    label: "Class performance dashboard",
    translationKey: "account.featureLabels.classDashboard",
  },
  {
    label: "Priority email support",
    translationKey: "account.featureLabels.priorityEmailSupport",
  },
  {
    label: "Priority chat support",
    translationKey: "account.featureLabels.priorityChatSupport",
  },
];

export const COMMON_USED_EMOJI = [
  "🥇",
  "🥈",
  "🏀",
  "⭐",
  "🔥",
  "💥",
  "👍",
  "🍀",
  "😫",
  "👨‍💻",
  "✌️",
  "☀️",
  "🧨",
  "😎",
  "🧛‍♂️",
  "😱",
  "🤦‍♀️",
  "😒",
  "🙌",
  "🙂",
  "🤔",
  "👩‍🏫",
  "😢",
];

export const INTENSITIES = [
  {
    label: "Easy",
    translationKey: "rubrics.intensity.easy",
    icon: "triangle-fill",
    color: "#28A745",
    value: "light",
  },
  {
    label: "Normal",
    translationKey: "rubrics.intensity.normal",
    icon: "square-fill",
    color: "#FFC107",
    value: "moderate",
  },
  {
    label: "Strict",
    translationKey: "rubrics.intensity.strict",
    icon: "pentagon-fill",
    color: "#DC3545",
    value: "intense",
  },
];

export const GRADE_LEVELS = [
  {
    label: "Elementary",
    translationKey: "rubrics.gradeLevel.elementary",
    icon: "triangle-fill",
    color: "#28A745",
    backgroundColor: "#fff",
    value: "elementary",
  },
  {
    label: "Middle School",
    translationKey: "rubrics.gradeLevel.middleSchool",
    icon: "square-fill",
    color: "#E27247",
    backgroundColor: "#fff",
    value: "middleschool",
  },
  {
    label: "High School",
    translationKey: "rubrics.gradeLevel.highSchool",
    icon: "pentagon-fill",
    color: "#2196F3",
    backgroundColor: "#fff",
    value: "highschool",
  },
  {
    label: "College",
    translationKey: "rubrics.gradeLevel.college",
    icon: "hexagon-fill",
    color: "#CE00DD",
    backgroundColor: "#fff",
    value: "college",
  },
];

export const FEEDBACK_LENGTH = [
  {
    label: "Detailed",
    translationKey: "rubrics.feedbackLength.detailed",
    value: "long",
  },
  {
    label: "Medium",
    translationKey: "rubrics.feedbackLength.medium",
    value: "normal",
  },
  {
    label: "Short",
    translationKey: "rubrics.feedbackLength.short",
    value: "concise",
  },
];

export const MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX = {
  elementary: 2,
  middleschool: 1,
  highschool: 0,
  college: 0,
};

export const ACCOUNT_LINKS = [
  {
    name: "My Account",
    translationKey: "account.links.myAccount",
    path: "/account",
    icon: "setting-edit-filter-gear",
  },
  {
    name: "Subscription",
    translationKey: "account.links.subscription",
    path: "/account?tab=subscription",
    icon: "users-group",
  },
  {
    name: "Integrations",
    translationKey: "account.links.integrations",
    path: "/account?tab=integrations",
    icon: "integrations",
  },
];

// ToT subdomain
export const ACCOUNT_SYNC_TASKS_LINK = {
  name: "Canvas Sync Tasks",
  translationKey: "account.links.canvasSyncTasks",
  path: "/account?tab=syncTasks",
  icon: "integrations",
};

export const WORKSPACE_LABELS = {
  yearly: {
    level1: "dashboard.workspace.labels.yearly.level1",
    level1Plural: "dashboard.workspace.labels.yearly.level1Plural",
    level2: "dashboard.workspace.labels.yearly.level2",
    level2Plural: "dashboard.workspace.labels.yearly.level2Plural",
    assignment: "dashboard.workspace.labels.yearly.assignment",
    addLevel1: "dashboard.workspace.labels.yearly.addLevel1",
    addLevel2: "dashboard.workspace.labels.yearly.addLevel2",
    addAssignment: "dashboard.workspace.labels.yearly.addAssignment",
  },
  class: {
    level1: "dashboard.workspace.labels.class.level1",
    level1Plural: "dashboard.workspace.labels.class.level1Plural",
    level2: "dashboard.workspace.labels.class.level2",
    level2Plural: "dashboard.workspace.labels.class.level2Plural",
    assignment: "dashboard.workspace.labels.class.assignment",
    addLevel1: "dashboard.workspace.labels.class.addLevel1",
    addLevel2: "dashboard.workspace.labels.class.addLevel2",
    addAssignment: "dashboard.workspace.labels.class.addAssignment",
  },
  subject: {
    level1: "dashboard.workspace.labels.subject.level1",
    level1Plural: "dashboard.workspace.labels.subject.level1Plural",
    level2: "dashboard.workspace.labels.subject.level2",
    level2Plural: "dashboard.workspace.labels.subject.level2Plural",
    assignment: "dashboard.workspace.labels.subject.assignment",
    addLevel1: "dashboard.workspace.labels.subject.addLevel1",
    addLevel2: "dashboard.workspace.labels.subject.addLevel2",
    addAssignment: "dashboard.workspace.labels.subject.addAssignment",
  },
  own: {
    level1: "dashboard.workspace.labels.own.level1",
    level1Plural: "dashboard.workspace.labels.own.level1Plural",
    level2: "dashboard.workspace.labels.own.level2",
    level2Plural: "dashboard.workspace.labels.own.level2Plural",
    addLevel1: "dashboard.workspace.labels.own.addLevel1",
    addLevel2: "dashboard.workspace.labels.own.addLevel2",
  },
};

export const WORKSPACE_TYPES = {
  yearly: {
    title: "dashboard.workspace.types.yearly.title",
    description: "dashboard.workspace.types.yearly.description",
  },
  class: {
    title: "dashboard.workspace.types.class.title",
    description: "dashboard.workspace.types.class.description",
  },
  subject: {
    title: "dashboard.workspace.types.subject.title",
    description: "dashboard.workspace.types.subject.description",
  },
  own: {
    title: "dashboard.workspace.types.own.title",
    description: "dashboard.workspace.types.own.description",
  },
};

export const DEFAULT_UI_STRUCTURES = {
  yearly: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  class: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  subject: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  own: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          canNotExpand: true,
        },
      ],
    },
  ],
};

export const WORKSPACE_PLACEHOLDERS = {
  yearly: {
    level1: new Date().getFullYear().toString(),
    level2: "dashboard.workspace.placeholders.yearly.level2",
    assignment: "dashboard.workspace.placeholders.yearly.assignment",
  },
  class: {
    level1: "dashboard.workspace.placeholders.class.level1",
    level2: "dashboard.workspace.placeholders.class.level2",
    assignment: "dashboard.workspace.placeholders.class.assignment",
  },
  subject: {
    level1: "dashboard.workspace.placeholders.subject.level1",
    level2: "dashboard.workspace.placeholders.subject.level2",
    assignment: "dashboard.workspace.placeholders.subject.assignment",
  },
  own: {
    level1: "dashboard.workspace.placeholders.own.level1",
    level2: "dashboard.workspace.placeholders.own.level2",
    assignment: "dashboard.workspace.placeholders.own.assignment",
  },
};

export const UPLOAD_TYPE_OPTIONS = [
  { value: "files", translationKey: "materials.createEditModal.selectFiles" },
  { value: "text", translationKey: "materials.createEditModal.enterText" },
];

export const USER_ROLE = {
  teacherEducator: {
    label: "Teacher / Educator",
    translationKey: "account.userRole.teacherEducator",
  },
  principalVicePrincipal: {
    label: "Principal / Vice-Principal",
    translationKey: "account.userRole.principalVicePrincipal",
  },
  schoolAdministrator: {
    label: "School Administrator",
    translationKey: "account.userRole.schoolAdministrator",
  },
  districtAdministrator: {
    label: "District Administrator",
    translationKey: "account.userRole.districtAdministrator",
  },
  iTTechnologyDirector: {
    label: "IT / Technology Director",
    translationKey: "account.userRole.itTechnologyDirector",
  },
  edTechCoordinator: {
    label: "EdTech Coordinator",
    translationKey: "account.userRole.edTechCoordinator",
  },
  curriculumCoordinator: {
    label: "Curriculum Coordinator",
    translationKey: "account.userRole.curriculumCoordinator",
  },
  student: { label: "Student", translationKey: "account.userRole.student" },
  other: { label: "Other", translationKey: "account.userRole.other" },
};

// External URLs
export const EXTERNAL_URLS = {
  SCHOOL_TRIAL: "https://essaygrader.fillout.com/school-trial",
};

// Rubric Creation Options
export const RUBRIC_CREATION_OPTIONS = [
  {
    creationType: "upload",
    icon: "upload",
    title: "Create From a File",
    details:
      "Have your rubric ready? Upload the file (.doc, .txt, .pdf) and we'll set it up",
    translationKey: "rubrics.creationOptions.upload",
  },
  {
    creationType: "ai",
    icon: "stars",
    title: "Build with AI",
    details: "Describe your assignment and let AI create a custom rubric",
    translationKey: "rubrics.creationOptions.ai",
  },
  {
    creationType: "template",
    icon: "bookmark",
    title: "Build From a Template",
    details: "Start with pre-built rubrics and customize them to your needs",
    translationKey: "rubrics.creationOptions.template",
  },
  {
    creationType: "scratch",
    icon: "scratch",
    title: "Build From Scratch",
    details: "Build your own rubric from the ground up with full control",
    translationKey: "rubrics.creationOptions.scratch",
  },
];

export const GC_SUBMISSION_STATES = Object.freeze({
  NEW: "NEW",
  RETURNED: "RETURNED",
  TURNED_IN: "TURNED_IN",
  CREATED: "CREATED",
  STATE_UNSPECIFIED: "STATE_UNSPECIFIED",
  RECLAIMED_BY_STUDENT: "RECLAIMED_BY_STUDENT",
  STUDENT_EDITED_AFTER_TURN_IN: "STUDENT_EDITED_AFTER_TURN_IN",
});

export const GC_SUBMISSION_STATES_LABELS = Object.freeze({
  [GC_SUBMISSION_STATES.NEW]: "imports.submissionStates.notStarted",
  [GC_SUBMISSION_STATES.RETURNED]: "imports.submissionStates.returned",
  [GC_SUBMISSION_STATES.TURNED_IN]: "imports.submissionStates.turnedIn",
  [GC_SUBMISSION_STATES.CREATED]: "imports.submissionStates.created",
  [GC_SUBMISSION_STATES.STATE_UNSPECIFIED]:
    "imports.submissionStates.unspecified",
  [GC_SUBMISSION_STATES.RECLAIMED_BY_STUDENT]:
    "imports.submissionStates.reclaimed",
  [GC_SUBMISSION_STATES.STUDENT_EDITED_AFTER_TURN_IN]:
    "imports.submissionStates.edited",
});

export const SUBMISSION_IMPORT_FAILURE_REASONS = {
  unsupportedState: "imports.failureReasons.unsupportedState",
  noFilesAttached: "imports.failureReasons.noFilesAttached",
  noGoogleDocsFiles: "imports.failureReasons.noGoogleDocsFiles",
  noSubmissionStarted: "imports.failureReasons.noSubmissionStarted",
};

// Metrics chart colors (mapped to design system palette)
export const METRICS_COLOR_STUDENT = "#8A3FFC"; // --color-stroke-primary / --colors-purple-500
export const METRICS_COLOR_CLASS_AVG = "#AEB8C2"; // --color-stroke-neutral-container / --colors-gray-300
export const METRICS_COLOR_STRONG_TEXT = "#308C4A"; // --color-text-semantic-onSuccess
export const METRICS_COLOR_NEED_SUPPORT_TEXT = "#DC3545"; // --color-text-semantic-onError

// Metrics grade tier thresholds
export const METRICS_GRADE_THRESHOLDS = {
  HIGH: 85,
  MEDIUM: 66,
};

// Metrics class grade distribution tier colors
export const METRICS_CLASS_TIER_COLORS = {
  high: "var(--color-data-chartGreen, #28A745)",
  medium: "var(--color-data-chartBlue, #60A5FA)",
  low: "var(--color-data-chartRedGrad, #FB8C84)",
};
